import { supabase } from '../../../lib/supabase';
import { Contact, FunnelStage, Lead } from '../../../types';
import { cleanPhone, comparePhones, contactPhoneKeyVariants } from '../../../utils/phoneUtils';
import {
    CONTACT_ROW_SELECT,
    mapContactRow,
} from '../../kanban/hooks/contactsHelpers';

export const OPPORTUNITY_SELECT = 'id, title, value, stage, origin, assigned_to, tags, observations, created_at, stage_entry_date, status, won_at, lost_at, lost_reason_id, loss_comments, contact_id, contact:contacts(name, email, whatsapp, phone)';
/** Alias estável — mesma fonte que ContactsProvider. */
export const CONTACT_SELECT = CONTACT_ROW_SELECT;
export const STAGE_SELECT = 'id, name, conversion_event, created_at, funnel_id, "order", deadline, color';

export function mergeLeadsById(local: Lead[], server: Lead[]): Lead[] {
    const map = new Map<string, Lead>();
    for (const l of local) map.set(l.id, l);
    for (const l of server) map.set(l.id, l);
    return Array.from(map.values()).sort((a, b) => {
        const dateA = new Date(a.entryDate || a.date || 0).getTime();
        const dateB = new Date(b.entryDate || b.date || 0).getTime();
        return dateB - dateA;
    });
}

export async function fetchFirstStageIdForFunnel(
    organizationId: string,
    funnelId: string
): Promise<string | null> {
    const { data, error } = await supabase
        .from('funnel_stages')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('funnel_id', funnelId)
        .order('order', { ascending: true })
        .limit(1)
        .maybeSingle();

    if (error) {
        console.error('[useContactPanelDeals] fetchFirstStageIdForFunnel:', error);
        return null;
    }
    return data?.id ?? null;
}

export function mapStageRow(stage: any): FunnelStage {
    return {
        id: stage.id,
        name: stage.name,
        conversionEvent: stage.conversion_event || 'none',
        createdAt: stage.created_at ? new Date(stage.created_at) : undefined,
        funnelId: stage.funnel_id || undefined,
        order: stage.order || undefined,
        deadline: stage.deadline || undefined,
        color: stage.color || null
    };
}

export function mapOpportunityRow(row: any): Lead {
    return {
        id: row.id,
        name: row.title || row.contact?.name || 'Negociação',
        whatsApp: row.contact?.whatsapp || row.contact?.phone || '',
        email: row.contact?.email || '',
        stage: row.stage,
        value: Number(row.value || 0),
        date: row.created_at || new Date().toISOString(),
        entryDate: row.created_at || new Date().toISOString(),
        stageEntryDate: row.stage_entry_date || undefined,
        tags: row.tags || [],
        origin: row.origin || 'WhatsApp',
        observations: row.observations || '',
        agent: row.assigned_to || null,
        contactId: row.contact_id || undefined,
        status: row.status || 'open',
        wonAt: row.won_at || undefined,
        lostAt: row.lost_at || undefined,
        lostReasonId: row.lost_reason_id || undefined,
        lossComments: row.loss_comments || undefined
    };
}

export { mapContactRow };

export function contactMatchesPhone(contact: Contact, rawPhone: string): boolean {
    const phoneVariants = contactPhoneKeyVariants(rawPhone);
    const contactPhone = cleanPhone(contact.whatsapp || contact.phone || '');
    return (
        (!!contact.phone_key && phoneVariants.includes(contact.phone_key)) ||
        comparePhones(contactPhone, rawPhone)
    );
}

/** Nome salvo na página de contatos. Sem contato, permanece o nome da conversa. */
export function conversationDisplayName(
    conversation: { id: string; name: string },
    contacts: Contact[],
): string {
    if (conversation.id.startsWith('ig:')) return conversation.name;
    const phone = cleanPhone(conversation.id);
    const saved = contacts.find((contact) => contactMatchesPhone(contact, phone))?.name?.trim();
    return saved || conversation.name;
}

export async function fetchContactById(
    organizationId: string,
    contactId: string
): Promise<Contact | null> {
    const { data, error } = await supabase
        .from('contacts')
        .select(CONTACT_SELECT)
        .eq('id', contactId)
        .eq('organization_id', organizationId)
        .maybeSingle();

    if (error || !data) return null;
    return mapContactRow(data);
}

export async function findExistingContactByPhone(
    organizationId: string,
    rawPhone: string
): Promise<Contact | null> {
    const phoneVariants = contactPhoneKeyVariants(rawPhone);

    if (phoneVariants.length > 0) {
        const { data } = await supabase
            .from('contacts')
            .select(CONTACT_SELECT)
            .eq('organization_id', organizationId)
            .in('phone_key', phoneVariants)
            .limit(1);

        if (data?.[0]) return mapContactRow(data[0]);
    }

    const searchTerm = rawPhone.length >= 10 ? rawPhone.slice(-10) : rawPhone;
    if (!searchTerm) return null;

    const { data, error } = await supabase
        .from('contacts')
        .select(CONTACT_SELECT)
        .eq('organization_id', organizationId)
        .or(`phone.ilike.%${searchTerm}%,whatsapp.ilike.%${searchTerm}%`)
        .limit(1);

    if (error) {
        console.error('[useContactPanelDeals] findExistingContactByPhone:', error);
        return null;
    }
    return data?.[0] ? mapContactRow(data[0]) : null;
}
